"use client";

import * as React from "react";

/**
 * Release the reserved scrollbar track on pages that do not scroll.
 *
 * `html { scrollbar-gutter: stable }` (globals.css) keeps every ordinary page
 * from shifting sideways when a dialog locks body scroll — it stays. But on a
 * page SHORTER than the viewport there is no scrollbar to reserve space for,
 * and the reserved strip paints the page background down the right-hand edge:
 * a white rectangle beside the footer. QA: "when the page is not vertically
 * scrollable, do not add the white rectangle on the right side".
 *
 * The measurement has to happen in the browser — CSS cannot ask whether the
 * document overflows — so this sets `data-no-scroll` on `<html>` while it does
 * not, and the rule in globals.css releases the gutter for exactly that case.
 * It re-measures on resize and whenever the document's size changes (fonts
 * landing, an image loading, a client navigation), and it removes the attribute
 * on unmount so nothing leaks into the next page.
 *
 * The e-paper readers do the same thing with `data-fullscreen-viewer`; this is
 * the general case of it.
 */
export function ShortPageGutter() {
  React.useEffect(() => {
    const root = document.documentElement;

    const measure = () => {
      // `scrollHeight > clientHeight` is the question the gutter answers.
      //
      // The tolerance is deliberately wider than sub-pixel rounding: releasing
      // the gutter makes the page ~15px WIDER, which re-wraps text and can move
      // the document height by a line either way. At a 1px threshold a page
      // sitting within a line-height of the viewport flips the attribute on
      // every re-measure and the strip flickers. Anything within a line of the
      // fold is treated as not scrolling, which is the state without the strip.
      const scrolls = root.scrollHeight > root.clientHeight + 24;
      if (scrolls) root.removeAttribute("data-no-scroll");
      else root.setAttribute("data-no-scroll", "");
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      root.removeAttribute("data-no-scroll");
    };
  }, []);

  return null;
}
